import { ChevronRightIcon } from "@heroicons/react/24/outline";
import { useQueryClient } from "@tanstack/react-query";
import clsx from "clsx";
import { format } from "date-fns";
import { useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";

import {
  Badge,
  Button,
  type Column,
  Heading,
  Paragraph,
  TabNavigation,
  Table,
} from "@fronterahealth/frontera-ui-components";

import {
  type GetOrganizationUsersOutput,
  type GetOrganizationsOutput,
  type GetOrphanedOrganizationUsersOutput,
  getBasecampOrganizationUsersOrphanedRetrieveQueryKey,
  useBasecampOrganizationUsersRetrieve,
  useBasecampOrganizationsRetrieve,
} from "@api/rest/types-and-hooks";

import { AddEditUser } from "./AddEditUser";
import { ClinicsTab } from "./ClinicsTab";
import { OrganizationSettingsTab } from "./OrganizationSettingsTab";
import { OrphanedProvidersTab } from "./OrphanedProvidersTab";

enum ActiveTab {
  Users = "users",
  Clinics = "clinic-listings",
  Settings = "settings",
  OrphanedProviders = "orphaned-providers",
}

enum InvitationStatus {
  Pending = "pending",
  Expired = "expired",
  Accepted = "accepted",
}

const USER_COLUMNS: Column<GetOrganizationUsersOutput>[] = [
  {
    title: "Name",
    dataIndex: "full_name",
    key: "full_name",
    render: (value) => (
      <Paragraph displayType="loud" size="small" colorType="primary">
        {(value as string | null) ?? "—"}
      </Paragraph>
    ),
  },
  {
    title: "Email address",
    dataIndex: "email",
    key: "email",
    render: (value) => <Paragraph size="small">{(value as string | null) ?? "—"}</Paragraph>,
  },
  {
    title: "Login count",
    dataIndex: "login_count",
    key: "login_count",
    render: (value) => <Paragraph size="small">{(value as number | null) ?? "—"}</Paragraph>,
  },
  {
    title: "Last login",
    dataIndex: "last_login",
    key: "last_login",
    render: (value) => (
      <Paragraph size="small">{value ? format(new Date(value as string), "MM/dd/yyyy h:mm a") : "-"}</Paragraph>
    ),
  },
  {
    title: "Invitation status",
    dataIndex: "invitation_status",
    key: "invitation_status",
    render: (value) => {
      const status = (value as InvitationStatus | null)?.toLowerCase() as InvitationStatus | null;
      if (status === InvitationStatus.Accepted)
        return <Badge className="whitespace-nowrap" appearance="success" text="Accepted" />;
      if (status === InvitationStatus.Expired)
        return <Badge className="whitespace-nowrap" appearance="error" text="Expired" />;
      if (status === InvitationStatus.Pending)
        return (
          <>
            <span className="pending-row-marker hidden" />
            <Badge className="whitespace-nowrap" appearance="warn" text="Pending" />
          </>
        );
      return <Badge className="whitespace-nowrap" appearance="neutral" text="Not Invited" />;
    },
  },
  {
    title: "Identity",
    dataIndex: "identities",
    key: "identities",
    render: (value) => (
      <Paragraph size="small">{(value as string[])?.length ? (value as string[]).join(", ") : "—"}</Paragraph>
    ),
  },
  {
    title: "FDM Provider",
    dataIndex: "fdm_provider",
    key: "fdm_provider",
    render: (value) => <Paragraph size="small">{value ? "✅" : "❌"}</Paragraph>,
  },
  {
    title: "FDM Caregiver",
    dataIndex: "fdm_caregiver",
    key: "fdm_caregiver",
    render: (value) => <Paragraph size="small">{value ? "✅" : "❌"}</Paragraph>,
  },
];

export const OrganizationDetail: React.FC = () => {
  const { orgId } = useParams<{ orgId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchParams] = useSearchParams();
  const tabParam = searchParams.get("tab");
  const initialTab =
    tabParam === ActiveTab.Clinics
      ? ActiveTab.Clinics
      : tabParam === ActiveTab.Settings
        ? ActiveTab.Settings
        : tabParam === ActiveTab.OrphanedProviders
          ? ActiveTab.OrphanedProviders
          : ActiveTab.Users;
  const [activeTab, setActiveTab] = useState<ActiveTab>(initialTab);
  const [addingUser, setAddingUser] = useState(false);
  const [restoringUser, setRestoringUser] = useState<GetOrphanedOrganizationUsersOutput | null>(null);

  const { data: orgData, refetch: refetchOrg } = useBasecampOrganizationsRetrieve(
    { id: orgId },
    { query: { enabled: !!orgId } },
  );

  const {
    data: usersData,
    isLoading: usersLoading,
    refetch: refetchUsers,
  } = useBasecampOrganizationUsersRetrieve({ organization_id: orgId! }, { query: { enabled: !!orgId } });

  const org: GetOrganizationsOutput | undefined =
    orgData?.status === 200
      ? Array.isArray(orgData.data)
        ? orgData.data.find((o) => String(o.id) === orgId)
        : orgData.data
      : undefined;

  const users: GetOrganizationUsersOutput[] =
    usersData?.status === 200 ? (Array.isArray(usersData.data) ? usersData.data : [usersData.data]) : [];

  const tabs = [
    {
      type: "component" as const,
      current: activeTab === ActiveTab.Users,
      name: "Users",
      Component: (
        <button type="button" onClick={() => setActiveTab(ActiveTab.Users)} className="py-4 text-sm text-text-primary">
          Users
        </button>
      ),
    },
    {
      type: "component" as const,
      current: activeTab === ActiveTab.Clinics,
      name: "Clinics",
      Component: (
        <button
          type="button"
          onClick={() => setActiveTab(ActiveTab.Clinics)}
          className="py-4 text-sm text-text-primary"
        >
          Clinics
        </button>
      ),
    },
    {
      type: "component" as const,
      current: activeTab === ActiveTab.Settings,
      name: "Settings",
      Component: (
        <button
          type="button"
          onClick={() => setActiveTab(ActiveTab.Settings)}
          className="py-4 text-sm text-text-primary"
        >
          Settings
        </button>
      ),
    },
    {
      type: "component" as const,
      current: activeTab === ActiveTab.OrphanedProviders,
      name: "Orphaned Providers",
      Component: (
        <button
          type="button"
          onClick={() => setActiveTab(ActiveTab.OrphanedProviders)}
          className="py-4 text-sm text-text-primary"
        >
          Orphaned Providers
        </button>
      ),
    },
  ];

  if ((addingUser || restoringUser) && org) {
    return (
      <AddEditUser
        organization={org}
        restoreTarget={restoringUser ?? undefined}
        onCancel={() => {
          setAddingUser(false);
          setRestoringUser(null);
        }}
        onSuccess={() => {
          setAddingUser(false);
          setRestoringUser(null);
          refetchUsers();
          // Remove moves a row INTO this list, restore moves one OUT of it —
          // either way it can go stale relative to the Users tab we just refetched.
          queryClient.invalidateQueries({
            queryKey: getBasecampOrganizationUsersOrphanedRetrieveQueryKey({ organization_id: orgId! }),
          });
        }}
      />
    );
  }

  return (
    <div className="flex flex-col bg-bg-default min-h-full">
      {/* Page header */}
      <div className="px-4 sm:px-8 lg:px-16 pt-6 sm:pt-8">
        <div className="flex items-center gap-2 mb-2">
          <button
            type="button"
            onClick={() => navigate("/organizations")}
            className="text-sm text-text-secondary hover:text-text-primary transition-colors"
          >
            Organizations
          </button>
          <ChevronRightIcon className="h-3 w-3 text-text-tertiary" />
          <span className="text-sm text-text-secondary">{org?.name}</span>
        </div>
        <Heading size="medium">{org?.name}</Heading>
      </div>

      {/* Tabs + Actions */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 px-4 sm:px-8 lg:px-16 pt-2 sm:pt-0">
        <TabNavigation tabs={tabs} />
        {activeTab === ActiveTab.Users && (
          <div className="pb-2 sm:pb-0">
            <Button
              variant="primary"
              buttonStyle="filled"
              size="sm"
              text="+ Add User"
              onClick={() => setAddingUser(true)}
            />
          </div>
        )}
      </div>

      {/* Tab content */}
      <div className="py-6">
        {activeTab === ActiveTab.Users && (
          <div className="overflow-x-auto">
            <div
              className={clsx(
                "min-w-[640px] px-4 sm:px-8 lg:px-16",
                "[&_tr:has(.pending-row-marker)]:cursor-default [&_tr:has(.pending-row-marker)]:hover:bg-transparent",
              )}
            >
              <Table<GetOrganizationUsersOutput>
                data={users}
                columns={USER_COLUMNS}
                loading={usersLoading}
                onRowClick={(row) => {
                  const status = row.invitation_status?.toLowerCase() as InvitationStatus | null;
                  if (status === InvitationStatus.Pending) return;
                  navigate(`/organizations/${orgId}/users/${row.auth0_id}`);
                }}
                showSearch
                showPagination
                defaultItemsPerPage={25}
                itemsPerPageOptions={[10, 25, 50]}
              />
            </div>
          </div>
        )}

        {activeTab === ActiveTab.Clinics && orgId && <ClinicsTab orgId={orgId} />}

        {activeTab === ActiveTab.Settings && orgId && (
          <OrganizationSettingsTab key={orgId} orgId={orgId} org={org} onSaved={() => refetchOrg()} />
        )}

        {activeTab === ActiveTab.OrphanedProviders && orgId && (
          <OrphanedProvidersTab orgId={orgId} onRestore={(row) => setRestoringUser(row)} />
        )}
      </div>
    </div>
  );
};
